import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { MarkdownGuidanceDetails } from '../../components/ui/MarkdownGuidanceDetails';
import { MarkdownGuidanceField } from '../../components/ui/MarkdownGuidanceField';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import {
  CompetencyAudience,
  PerformanceIndicator,
  PerformanceIndicatorUpsertPayload,
  createPerformanceIndicator,
  deletePerformanceIndicator,
  listPerformanceIndicators,
  updatePerformanceIndicator,
} from '../../services/peoplePerformanceApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type IndicatorFormState = Omit<PerformanceIndicatorUpsertPayload, 'sortOrder'>;

const AUDIENCE_OPTIONS: { value: CompetencyAudience; label: string }[] = [
  { value: 'Colaborador', label: 'Colaborador' },
  { value: 'Gestor', label: 'Gestor' },
  { value: 'Executivo', label: 'Executivo' },
];

const emptyForm: IndicatorFormState = {
  name: '',
  description: '',
  guidanceMarkdown: '',
  audience: 'Colaborador',
  isActive: true,
};

function audienceLabel(value?: string | null): string {
  return AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

function buildIndicatorPayload(item: PerformanceIndicator, sortOrder: number): PerformanceIndicatorUpsertPayload {
  return {
    name: item.name,
    description: item.description,
    guidanceMarkdown: item.guidanceMarkdown,
    audience: item.audience,
    sortOrder,
    isActive: item.isActive,
  };
}

export function PerformanceIndicatorsPage() {
  const { confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.competencies');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [items, setItems] = useState<PerformanceIndicator[]>([]);
  const [form, setForm] = useState<IndicatorFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [audienceFilter, setAudienceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const result = await listPerformanceIndicators({
        pageSize: 100,
        audience: audienceFilter || undefined,
      });
      setItems(result.items);
    } catch {
      toast.error('Falha ao carregar indicadores.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updatePerformanceIndicator(item.id, buildIndicatorPayload(item, sortOrder));
    },
    onReordered: () => loadData(),
  });

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: PerformanceIndicator): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      guidanceMarkdown: item.guidanceMarkdown ?? '',
      audience: item.audience || 'Colaborador',
      isActive: item.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: PerformanceIndicatorUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(items),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updatePerformanceIndicator(editingId, payload);
        toast.success('Indicador atualizado.');
        resetForm();
      } else {
        await createPerformanceIndicator(payload);
        toast.success('Indicador criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao salvar indicador.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    const item = items.find((current) => current.id === id);
    if (
      !(await confirmDelete({
        title: 'Excluir indicador',
        message: item ? `Excluir "${item.name}"?` : 'Excluir este indicador?',
      }))
    ) {
      return;
    }
    try {
      await deletePerformanceIndicator(id);
      toast.success('Indicador excluído.');
      await loadData();
    } catch {
      toast.error('Falha ao excluir indicador.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Indicadores"
        description="Catálogo de indicadores de desempenho usados nas avaliações de pessoas."
        actions={(
          <Link to="/people/competencies" className="erp-btn-secondary text-sm">
            Competências
          </Link>
        )}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar indicador' : 'Novo indicador'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          isEditing={!!editingId}
          createSummary="Clique para expandir e cadastrar um novo indicador"
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input className="erp-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
              <label className="erp-label">
                Público-alvo
                <select
                  className="erp-input"
                  value={form.audience}
                  required
                  onChange={(event) => setForm({ ...form, audience: event.target.value as CompetencyAudience })}
                >
                  {AUDIENCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Descrição
              <textarea className="erp-input mt-1" value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            <div className="mt-3">
              <MarkdownGuidanceField
                value={form.guidanceMarkdown ?? ''}
                onChange={(guidanceMarkdown) => setForm({ ...form, guidanceMarkdown })}
              />
            </div>
            <label className="erp-label-inline mt-3">
              <input type="checkbox" checked={form.isActive ?? true} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
              Ativo
            </label>
            <FormActionBar onCancel={() => collapseForm(resetForm)} className="erp-btn-actions mt-4">
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium text-secondary-800">Listagem</p>
        <select
          className="erp-input-compact max-w-xs"
          value={audienceFilter}
          onChange={(event) => setAudienceFilter(event.target.value)}
          aria-label="Filtrar por público-alvo"
        >
          <option value="">Todos os públicos</option>
          {AUDIENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
          <table className="erp-table">
            <thead>
              <tr>
                {canWrite ? <th className="w-10" /> : null}
                <th>Nome</th>
                <th>Público</th>
                <th>Status</th>
                <th className="w-24">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 5 : 4} className="text-center text-secondary-500">
                    Nenhum indicador cadastrado.
                  </td>
                </tr>
              ) : (
                sortedItems.map((item, index) => (
                  <tr key={item.id}>
                    {canWrite ? (
                      <td>
                        <SortOrderControls
                          canMoveUp={index > 0}
                          canMoveDown={index < sortedItems.length - 1}
                          disabled={moving}
                          onMoveUp={() => void move(index, 'up')}
                          onMoveDown={() => void move(index, 'down')}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="font-medium text-secondary-900">{item.name}</div>
                      <MarkdownGuidanceDetails value={item.guidanceMarkdown} />
                    </td>
                    <td>{audienceLabel(item.audience)}</td>
                    <td>
                      <ActiveStatusBadge active={item.isActive} />
                    </td>
                    <td>
                      {canWrite ? (
                        <div className="flex gap-1">
                          <EditIconButton label="Editar indicador" onClick={() => startEdit(item)} />
                          <DeleteIconButton label="Excluir indicador" onClick={() => void handleDelete(item.id)} />
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
